/**
 * Accordion functionality with modern A11y standards
 *
 * Implements ARIA attributes and keyboard navigation
 * following WCAG 2.1 guidelines for accordion components.
 */

document.addEventListener("DOMContentLoaded", function () {
  const accordions = document.querySelectorAll('[data-behavior="accordion"]');

  accordions.forEach(initAccordion);
  initCopyLinks();

  // Open the targeted item on initial page load
  if (window.location.hash) {
    openItemByHash(window.location.hash.slice(1));
  }

  // Open the targeted item when the hash changes (e.g. browser back/forward or
  // clicking a copied link while already on the page)
  window.addEventListener("hashchange", () => {
    if (window.location.hash) {
      openItemByHash(window.location.hash.slice(1));
    }
  });
});

function initAccordion(accordion) {
  const items = accordion.querySelectorAll(
    ".wp-block-generateprop-accordion-item",
  );

  // Per APG: avoid role="region" when there are 6 or more expandable sections
  const useRegionRole = items.length < 6;

  items.forEach((item, index) => {
    const trigger = item.querySelector(".accordion-trigger");
    const content = item.querySelector(".accordion-content");

    if (!trigger || !content) return;

    // Set up ARIA IDs
    const itemId = `accordion-item-${Date.now()}-${index}`;
    const triggerId = `${itemId}-trigger`;
    const contentId = `${itemId}-content`;

    trigger.setAttribute("id", triggerId);
    trigger.setAttribute("aria-controls", contentId);

    content.setAttribute("id", contentId);
    content.setAttribute("aria-labelledby", triggerId);

    if (useRegionRole) {
      content.setAttribute("role", "region");
    }

    // Read initial open state from aria-expanded set in static HTML
    const isInitiallyOpen = trigger.getAttribute("aria-expanded") === "true";

    if (isInitiallyOpen) {
      content.classList.remove("hidden");
    } else {
      content.setAttribute("hidden", "");
    }

    // Click handler
    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      toggleItem(item, trigger, content);
    });

    // Keyboard handler
    trigger.addEventListener("keydown", (e) => {
      switch (e.key) {
        case "Enter":
        case " ": // Space
          e.preventDefault();
          toggleItem(item, trigger, content);
          break;
        case "ArrowDown":
          e.preventDefault();
          focusNextTrigger(items, index);
          break;
        case "ArrowUp":
          e.preventDefault();
          focusPreviousTrigger(items, index);
          break;
        case "Home":
          e.preventDefault();
          focusFirstTrigger(items);
          break;
        case "End":
          e.preventDefault();
          focusLastTrigger(items);
          break;
      }
    });
  });
}

function toggleItem(item, trigger, content) {
  const isExpanded = trigger.getAttribute("aria-expanded") === "true";

  if (isExpanded) {
    // Close the item
    trigger.setAttribute("aria-expanded", "false");
    content.setAttribute("hidden", "");
    item.classList.remove("is-open");
  } else {
    // Open the item
    trigger.setAttribute("aria-expanded", "true");
    content.removeAttribute("hidden");
    item.classList.add("is-open");
  }
}

function openItemByHash(hash) {
  const item = document.getElementById(hash);
  if (!item || !item.classList.contains("wp-block-generateprop-accordion-item")) return;

  const trigger = item.querySelector(".accordion-trigger");
  const content = item.querySelector(".accordion-content");
  if (!trigger || !content) return;

  // Open the item if it isn't already
  if (trigger.getAttribute("aria-expanded") !== "true") {
    trigger.setAttribute("aria-expanded", "true");
    content.removeAttribute("hidden");
    item.classList.add("is-open");
  }

  // Scroll into view after a tick so layout is settled
  requestAnimationFrame(() => {
    item.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

function focusNextTrigger(items, currentIndex) {
  const nextIndex = currentIndex + 1 < items.length ? currentIndex + 1 : 0;
  const nextTrigger = items[nextIndex].querySelector(".accordion-trigger");
  if (nextTrigger) nextTrigger.focus();
}

function focusPreviousTrigger(items, currentIndex) {
  const prevIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : items.length - 1;
  const prevTrigger = items[prevIndex].querySelector(".accordion-trigger");
  if (prevTrigger) prevTrigger.focus();
}

function focusFirstTrigger(items) {
  const firstTrigger = items[0].querySelector(".accordion-trigger");
  if (firstTrigger) firstTrigger.focus();
}

function focusLastTrigger(items) {
  const lastTrigger =
    items[items.length - 1].querySelector(".accordion-trigger");
  if (lastTrigger) lastTrigger.focus();
}

function initCopyLinks() {
  const copyButtons = document.querySelectorAll(".accordion-copy-link");

  copyButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const anchorId = button.getAttribute("data-anchor-id");
      if (!anchorId) return;

      const url =
        window.location.origin +
        window.location.pathname +
        "#" +
        anchorId;

      navigator.clipboard.writeText(url).then(() => {
        const label = button.querySelector(".accordion-copy-link-label");
        const originalLabel = label ? label.textContent : null;

        button.classList.add("is-copied");
        button.setAttribute("aria-label", "Copied!");
        if (label) label.textContent = "Copied!";

        setTimeout(() => {
          button.classList.remove("is-copied");
          button.setAttribute("aria-label", "Copy link to this section");
          if (label && originalLabel) label.textContent = originalLabel;
        }, 2000);
      });
    });
  });
}
